import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

export const SubscriptionCard = () => {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/collectors/me");
      setProfile(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(msg);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!profile) return null;
  const { subscription_active: active, subscription_cancelled: cancelled, subscription_until: until } = profile;

  return (
    <div className="card-soft" data-testid="subscription-card">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#2D6A4F]" /> Subscription</h2>

      {active && !cancelled && (
        <>
          <div className="flex items-center gap-2 text-[#2D6A4F]" data-testid="sub-status-active"><ShieldCheck className="w-4 h-4" /><span className="font-medium text-sm">Active — $10.99/mo</span></div>
          <p className="text-xs text-[#5C6B64] mt-2">Renews {fmtDate(until)}. You're being connected to customers.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="dashboard-cancel-subscription" className="btn-secondary mt-4 text-xs py-2 text-[#B91C1C] border-[#B91C1C]/30">Cancel subscription</button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="dashboard-cancel-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                <AlertDialogDescription>
                  You'll keep receiving customers until <span className="font-medium">{fmtDate(until)}</span>. After that your profile stops being connected to customers and won't renew. You won't be charged again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="dashboard-cancel-dismiss">Keep subscription</AlertDialogCancel>
                <AlertDialogAction data-testid="dashboard-cancel-confirm" onClick={() => act("/payments/cancel-subscription", "Subscription cancelled — it won't renew.")} disabled={busy} className="bg-[#B91C1C] hover:bg-[#991818]">Yes, cancel</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {active && cancelled && (
        <>
          <div className="flex items-center gap-2 text-[#B45309]" data-testid="sub-status-cancelled"><AlertTriangle className="w-4 h-4" /><span className="font-medium text-sm">Cancelled — won't renew</span></div>
          <p className="text-xs text-[#5C6B64] mt-2">Access continues until {fmtDate(until)}.</p>
          <button data-testid="dashboard-reactivate-subscription" onClick={() => act("/payments/reactivate-subscription", "Subscription resumed.")} disabled={busy} className="btn-primary mt-4 text-xs py-2 inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Resume subscription</button>
        </>
      )}

      {!active && (
        <>
          <div className="flex items-center gap-2 text-[#8A9A86]" data-testid="sub-status-inactive"><AlertTriangle className="w-4 h-4" /><span className="font-medium text-sm">No active subscription</span></div>
          <p className="text-xs text-[#5C6B64] mt-2">Subscribe to start being connected to customers.</p>
          <Link to="/subscribe" data-testid="dashboard-subscribe-cta" className="btn-accent mt-4 text-xs py-2 inline-flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> Subscribe — $10.99/mo</Link>
        </>
      )}
    </div>
  );
};
